import {observable} from "mobx"
import {Action, History, Location} from "history"
import moment from "moment-timezone"
import {Gate} from "./lib/gate/Gate"
import {api_response, NewCalendarEvent, Visitor} from "./lib/gate/interfaces"
import {
    authService,
    BaseService,
    calendarService,
    coreSocket,
    departmentsService,
    generalSettingsService,
    rolePermissionService,
    settingsService,
    smsService,
    activityLogsService,
    userService,
    userServiceV3,
    userUiTableColumnSettingsService
} from "services"
import {Auth} from "types/auth"
import {Settings} from "types/settings"
import {GeneralSettingField, StartWeekOn} from "types/settings/general-setting"
import {syncAntdMomentWeekStart} from "helpers/antdWeekStart"
import {enAU, es} from "date-fns/locale"
import {format} from "date-fns"
import enLocale from "@fullcalendar/core/locales/en-gb"
import esLocale from "@fullcalendar/core/locales/es"
import {SlugHelper} from "SlugHelper"
import {ProfilePermissionsDetails} from "@edular/permissions"
import {getUnreadCountBadge, store} from "helpers"
import * as Sentry from "@sentry/react"
import {ViewType} from "sections/shared/students"
import {FilterKey} from "types/filter"
import {UiTableColumnSettingsData, UserUiTableColumnSettingRow} from "types/userUiTableColumnSettings"
import {SOCKET_PATH_EDULAR_CORE} from "services/SocketClient"
import {get, head, isString, orderBy, pick} from "lodash"
import {KEY_PRIORITY_COLLAPSIBLE, UNLIMITED_PAGE_SIZE} from "data/constants"
import {ActivityLogType} from "types/student-activity-log"
import i18n from "config/i18n"
import {Communication} from "types/communication"

export class Model {
    @observable
    public visitor: Visitor

    @observable
    public slug: string

    @observable
    public isLoggedIn: boolean = false

    @observable
    public authToken: string

    @observable
    public profileId: number

    @observable
    public path: string

    @observable
    public location: Location

    @observable
    public prevLocation: Location

    @observable
    public currentLocation: Location

    @observable
    public user: Auth.UserProfile

    @observable
    public appSettings: any

    @observable
    public activeSchedules = []

    @observable
    public selectedLocations: api_response.Locations[]

    @observable
    public locations: api_response.Locations[] = []

    @observable
    public allPrograms: api_response.Program[] = []

    @observable
    public selectedPrograms: api_response.Program[] = []

    @observable
    public allCourses: api_response.Courses[] = []

    @observable
    public selectedCourses: api_response.Courses[] = []

    @observable
    public resourceTypes: any

    @observable
    public departments: any = []

    @observable
    public fullScreenMode: boolean = false

    @observable
    public calendars: any[] = []

    @observable
    public calendarEvents: NewCalendarEvent[] = []

    @observable
    public timeZones: any[] = []

    @observable
    public dateFormat: any

    @observable
    public tableView: any

    @observable
    public calendarView: any

    @observable
    public dateTimeFormat: any

    @observable
    public timeFormat: string

    @observable
    public courseTypes: any

    @observable
    public contactDetail: any = {}

    @observable
    public contactDetailRoomId: any = {}

    @observable
    public allowedDocumentFormats: any = null

    @observable
    public fieldLabels: Record<string, Settings.FieldLabel> = {}

    @observable
    public calendarEventSelected: NewCalendarEvent = {} as NewCalendarEvent

    @observable
    public clientSetting: Settings.ClientSetting = {} as Settings.ClientSetting

    /** First day of academic week: 0=Sunday, 1=Monday, 6=Saturday (string from general settings). */
    @observable
    public startWeekOn: string = StartWeekOn.Sunday

    @observable
    public menu = {infoBar: true, sidebar: true}

    @observable
    public isSubmittingTaskFromEmail = false

    @observable
    public securityLevelEncoded = ""

    @observable
    public smsPhoneNumbers = []

    @observable
    public canSendSms = false
    @observable
    public canSendDirectSms = false
    @observable
    public smsDepartments: number[] = []

    @observable
    public smsAllPhoneNumbers: Array<{
        smsPhoneNumbersId: number
        areaCode?: number
        type: "toll_free" | "local"
        phone: string
        campuses: Array<{id: number; name: string}>
        departments: Array<{departmentId: number; name: string}>
        user?: {
            id: number
            firstName: string
            lastName: string
        }
        isGlobal?: number
        departmentForDialer?: {
            departmentId: number
            name: string
        }
        departmentIdForDialer?: number
    }> = []

    @observable
    public myPermissions: ProfilePermissionsDetails

    @observable
    public departmentPermissions: any

    @observable
    public calendarOptions: any = {
        month: "",
        year: "",
        nextMonth: -1,
        prevMonth: -1
    }

    @observable
    public photo: any

    public readonly history: History

    @observable
    public windowFocused = true

    @observable
    public filterUserList: any = {
        search: ""
    }

    @observable
    public uiTableColumnSettingsByPageKey: Record<string, {columnData: UiTableColumnSettingsData}> = {}

    public isSsoEnabled: boolean

    private screenCaptureInterval: any

    // chat & calendar
    @observable
    public departmentsChats: any = []

    @observable
    public users: any = {}

    @observable
    public usersNotFound: {[key: string]: number} = {}

    @observable
    public chatOptions: any = {}

    @observable
    public viewOtherUserCalendar = null

    // end chat & calendar

    constructor(private gate: Gate, history: History) {
        this.history = history
        history.listen(this.onHistoryChange.bind(this))
        this.onHistoryChange(history.location, "PUSH")
        this.courseTypes = this.initCourseTypes()
        this.generateSlug()
    }

    public loadSettings = async () => {
        try {
            this.clientSetting = await settingsService.getClientSettings({slug: this.slug})
        } catch (e) {
            throw e
        }
    }

    public loadStartWeekOn = async () => {
        try {
            const [setting] = await generalSettingsService.getSettings([GeneralSettingField.StartWeekOn])
            this.setStartWeekOn(String(setting?.value ?? StartWeekOn.Sunday))
        } catch (e) {
            this.setStartWeekOn(StartWeekOn.Sunday)
        }
    }

    public setStartWeekOn = (value: string) => {
        this.startWeekOn = String(value ?? StartWeekOn.Sunday)
        syncAntdMomentWeekStart(this.getStartWeekOn())
    }

    /** Numeric weekday for FullCalendar/DayPicker (0=Sunday … 6=Saturday). */
    public getStartWeekOn(): number {
        const parsed = Number(this.startWeekOn)
        if (Number.isNaN(parsed)) return 0
        return ((parsed % 7) + 7) % 7
    }

    public loadAllowedDocumentFormats = async () => {
        const formats = await settingsService.getAllowedDocumentFormats()
        this.allowedDocumentFormats = formats.map((allowedFormat) => allowedFormat.mimetype).join(", ")
    }

    public loadFieldLabels = async () => {
        const fieldLabels = await userService.getLabels()
        this.fieldLabels = fieldLabels
    }

    public getAllowedDocumentFormats = () => {
        return this.allowedDocumentFormats
    }

    public updatePublicBranding = async (publicBranding: Settings.PublicBranding) => {
        this.clientSetting.branding = publicBranding
    }

    public getScreenShot = () => {
        const video: any = document.getElementById("hiddenVideo")
        const canvas: any = document.getElementById("hiddenCanvas")

        if (!video) {
            return
        }
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        canvas.getContext("2d").drawImage(video, 0, 0, video.videoWidth, video.videoHeight) // for drawing the video element on the canvas

        return canvas.toDataURL("image/jpeg")
    }

    public screenCapture = async (asyncUpdateFunction) => {
        this.screenCaptureInterval = setInterval(async () => {
            const base64Image = this.getScreenShot()
            await asyncUpdateFunction(base64Image)
        }, 3000)
    }

    public stopScreenCapture = async () => {
        clearInterval(this.screenCaptureInterval)
    }

    public loadMyPermissions = async () => {
        try {
            const [myPermissions, departmentPermissions] = await Promise.all([
                rolePermissionService.getMyPermissions(),
                rolePermissionService.getDepartmentPermissions()
            ])
            this.myPermissions = myPermissions
            this.departmentPermissions = departmentPermissions
        } catch (error) {
            throw error
        }
    }

    public updateUserTimeZone = async (): Promise<void> => {
        if (this.isLoggedIn && this.user?.id && !this.isStudent()) {
            const timeZone = moment.tz.guess()
            await userServiceV3.update({
                id: this.user.id,
                timeZone
            })
        }
    }

    public acceptPrivacyPolicy = async (): Promise<void> => {
        if (this.isLoggedIn && this.user?.id) {
            await userServiceV3.acceptPrivacyPolicy()
        }
    }

    public generateSlug() {
        this.slug = SlugHelper.generate()
    }

    public getSlug() {
        return this.slug
    }

    private initCourseTypes() {
        return [
            {
                id: 1,
                name: "Hours"
            },
            {
                id: 2,
                name: "Credit"
            },
            {
                id: 3,
                name: "Each"
            }
        ]
    }

    public async loadSmsPhoneNumbers() {
        const {phones} = await smsService.me({})
        const {data: allPhones} = await smsService.getNumbers({
            range: {
                limit: UNLIMITED_PAGE_SIZE,
                offset: 0
            }
        })
        this.smsAllPhoneNumbers = allPhones ?? []
        this.smsPhoneNumbers = phones
        this.smsDepartments = []
        this.canSendSms = !!this.smsPhoneNumbers.length
        this.smsPhoneNumbers.forEach((phone) => {
            if (phone.smsPhoneNumbersUsersId) {
                this.canSendDirectSms = true
            } else {
                this.smsDepartments.push(phone.departmentId)
            }
        })
    }

    public setUserStorage(user) {
        const profiles = (user.profiles || []).map(({id, isAccessDisabled, active, type, campuses}) => ({
            id,
            isAccessDisabled,
            active,
            type,
            campuses: campuses?.map((campus) => ({id: campus.id, name: campus.name})) || []
        }))
        const data: any = pick(user, [
            "id",
            "firstName",
            "lastName",
            "emails",
            "dateFormat",
            "filterMemory",
            "dateFormat",
            "timeFormat"
        ])
        data.profiles = profiles
        store.set("user", data)
    }

    public async loadUserInfo() {
        try {
            const userInfo = store.get("user")
            const user = await userServiceV3.getOne(userInfo.id)
            this.user = user
            this.setUserStorage(user)
        } catch (error) {
            //
        }
    }

    public loadSession() {
        const token = store.get("authToken")
        const user = store.get("user")
        const serializedProfileId = store.get("profileId")
        const profileId = serializedProfileId ? +serializedProfileId : null

        if (user) {
            const userId = String(user.id)
            const email = user.emails?.[0]?.email
            const username = `${user.firstName} ${user.lastName}`
            const currentProfileId = String(profileId)

            Sentry.setUser({
                id: userId,
                email: email,
                username,
                profileId: currentProfileId,
                slug: this.slug
            })
        }

        this.setUserToken(user, token, profileId)
    }

    public loadSocket() {
        const token = store.get("authToken")
        const user = store.get("user")
        const serializedProfileId = store.get("profileId")
        const profileId = serializedProfileId ? +serializedProfileId : null

        if (user) {
            coreSocket.setUserId(user.id)
            coreSocket.setProfileId(profileId)
            coreSocket.setAccessToken(token)
            coreSocket.initSocket(
                `${process.env.REACT_APP_EDULAR_CORE_API_BASE_URL}/${this.slug}`,
                SOCKET_PATH_EDULAR_CORE
            )
        }
    }

    private setUserToken(user: Auth.UserProfile, token, profileId: number) {
        if (user && token && profileId) {
            try {
                if (user && user.photo && isString(user.photo)) {
                    user.photo = JSON.parse(user.photo)
                }
                this.user = user
                this.profileId = profileId
                this.authToken = token
                this.isLoggedIn = true
                BaseService.setAuthToken(token)
                BaseService.setProfileId(profileId)
            } catch (e) {
                store.remove("user")
            }
        }
    }

    public async logout() {
        const deviceId = store.get("deviceId")
        activityLogsService
            .createActivityLogs([
                {
                    time: new Date(),
                    userId: this.user.id,
                    type: ActivityLogType.AuthLogOutFromWeb,
                    targetId: this.user.id,
                    data: {
                        user: pick(this.user, ["id", "customUserId", "profileId", "firstName", "lastName", "fullName"])
                    }
                }
            ])
            .catch(console.error)
        await this.loadSettings()
        store.clearAll()
        store.set("deviceId", deviceId)
        const channel = new BroadcastChannel("auth")
        channel.postMessage("logout")
        channel.close()
        this.user = null
        this.profileId = null
        this.authToken = null
        this.isLoggedIn = false
        this.uiTableColumnSettingsByPageKey = {}

        Sentry.setUser(null)
    }

    public getUserProfileType() {
        let currentProfile = null
        if (this.user) {
            currentProfile = this.user.profiles.find((profile) => profile.id === this.profileId)
        }
        return currentProfile && currentProfile.type
    }

    public getCurrentUserProfile() {
        return this.user ? this.user.profiles.find((profile) => profile.id === this.profileId) : null
    }

    public getUserProfileCampusIds(): number[] {
        const campusIds = (this.user?.profiles || []).flatMap((profile) =>
            (profile.campuses || []).map((campus) => campus.id)
        )
        return [...new Set(campusIds)]
    }

    public getUserProfileState() {
        let currentProfile = null
        if (this.user) {
            currentProfile = this.user.profiles.find((profile) => profile.id === this.profileId)
        }
        return currentProfile && currentProfile.state
    }

    public getLocale(): string {
        return store.get("locale") || "en"
    }

    public getDateFnsLocale() {
        const locales = {
            en: enAU,
            de: es
        }
        return locales[this.getLocale()]
    }

    public getFullCalendarLocale() {
        const locales = {
            en: enLocale,
            de: esLocale
        }
        return locales[this.getLocale()]
    }

    public getUserEmail() {
        let email = null
        if (this.user) {
            email = this.user.emails.find((email) => email && email.isPrimary === 1)
        }
        return email && email.email
    }

    public setDisplayLanguage(locale: string): void {
        i18n.changeLanguage(locale)

        store.set("locale", locale)
    }

    public updateUser(key: string, value: any): void {
        this.user = {...this.user, [key]: value}
    }

    public loadPhoto(photo: string | null) {
        const token = store.get("authToken")
        if (this.user && token) {
            if (photo) {
                const photoParsed = JSON.parse(photo)
                this.user = {...this.user, photo: photoParsed}
                this.photo = photoParsed.original
            } else {
                this.user = {...this.user, photo}
                this.photo = photo
            }
        }
    }

    public isAdmin() {
        return !!this.user?.profiles?.find(
            (profile) => this.profileId === profile.id && profile.active && profile.type === Auth.UserProfileType.Admin
        )
    }

    public isStaffOrAdmin() {
        let currentProfile = null
        if (this.user) {
            currentProfile = this.user.profiles?.find((profile) => profile.id === this.profileId)
        }
        if (
            currentProfile &&
            (currentProfile.type === Auth.UserProfileType.Staff || currentProfile.type === Auth.UserProfileType.Admin)
        ) {
            return true
        }
        return false
    }

    public isStudent() {
        return !!this.user?.profiles?.find(
            (profile) =>
                this.profileId === profile.id && profile.active && profile.type === Auth.UserProfileType.Student
        )
    }

    public isStaff() {
        return !!this.user?.profiles?.find(
            (profile) => this.profileId === profile.id && profile.active && profile.type === Auth.UserProfileType.Staff
        )
    }

    public isOther() {
        return !!this.user?.profiles?.find(
            (profile) => this.profileId === profile.id && profile.active && profile.type === Auth.UserProfileType.Others
        )
    }

    public isPaymentFormVisible() {
        return ["naa", "nau", "hhs", "sandbox", "mdt"].includes(this.slug)
    }

    public changeProfile(profileIndex: number) {
        const newProfile = this.user?.profiles.find((profile) => profile && profile.id === profileIndex)

        if (newProfile) {
            store.set("profileId", newProfile.id.toString())
            BaseService.setProfileId(newProfile.id)
            this.profileId = newProfile.id

            const userId = String(this.user.id)
            const email = this.user.emails?.[0]?.email
            const username = `${this.user.firstName} ${this.user.lastName}`
            const currentProfileId = String(newProfile.id)

            Sentry.setUser({
                id: userId,
                email,
                username,
                profileId: currentProfileId,
                slug: this.slug
            })
            window.location.reload()
        }
    }

    public getUserDateFormat() {
        let userDateFormat: string
        if (this.user) {
            const {dateFormat} = this.user
            userDateFormat = dateFormat || this.loadDateFormat()
        }
        return userDateFormat
    }

    public getUserTimeFormat() {
        let userTimeFormat = "HH:mm"
        if (this.user) {
            const {timeFormat} = this.user
            userTimeFormat = timeFormat || this.loadTimeFormat()
            if (userTimeFormat === Auth.TimeFormat.Time_12) {
                return "hh:mm A"
            }
            return "HH:mm"
        }
        return userTimeFormat
    }

    public getUserTimezone() {
        return moment.tz.guess() || "America/New_York"
    }

    public getUserTableView() {
        let userTableView
        if (this.user) {
            const {tableView} = this.user
            userTableView = tableView || this.loadTableViewDefault()
        }
        return userTableView
    }

    public getUserCalendarView() {
        let userCalendarView
        if (this.user) {
            const {calendarView} = this.user
            userCalendarView = calendarView || this.loadCalendarViewDefault()
        }
        return userCalendarView
    }

    private pickNonEmptyLocalColsOrServer(localCols: unknown, serverCols: (number | string)[]): (number | string)[] {
        return Array.isArray(localCols) && localCols.length > 0 ? localCols : serverCols
    }

    private resolveColOrdFromLocalAndServer(
        local: Record<string, any>,
        serverColOrd?: UiTableColumnSettingsData["colOrd"]
    ): UiTableColumnSettingsData["colOrd"] | undefined {
        if (!("colOrd" in local)) {
            return serverColOrd
        }
        const co = local.colOrd
        const hasLocalColOrd =
            co && typeof co === "object" && co.idx !== undefined && co.idx !== null && typeof co.order === "string"
        return hasLocalColOrd ? co : undefined
    }

    private pickColArraysForLocalMerge(
        cur: Record<string, any>,
        cd: UserUiTableColumnSettingRow["columnData"]
    ): {colsAct: (number | string)[]; colsIdx: (number | string)[]} {
        const pick = (key: "colsAct" | "colsIdx") => {
            const local = cur[key]
            const server = cd?.[key]
            if (Array.isArray(local) && local.length > 0) {
                return local
            }
            if (Array.isArray(server)) {
                return server
            }
            return local
        }
        return {colsAct: pick("colsAct"), colsIdx: pick("colsIdx")}
    }

    private columnDataFromSettingRow(row: UserUiTableColumnSettingRow): UiTableColumnSettingsData {
        return {
            colsAct: Array.isArray(row.columnData?.colsAct) ? row.columnData.colsAct : [],
            colsIdx: Array.isArray(row.columnData?.colsIdx) ? row.columnData.colsIdx : [],
            colOrd: row.columnData?.colOrd
        }
    }

    private buildUiTableColumnSettingsByTableKey(
        listRows: UserUiTableColumnSettingRow[]
    ): Record<string, {columnData: UiTableColumnSettingsData}> {
        const next: Record<string, {columnData: UiTableColumnSettingsData}> = {}
        for (const row of listRows) {
            if (!row?.tableKey) {
                continue
            }
            const tableKeyStr = String(row.tableKey)
            next[tableKeyStr] = {columnData: this.columnDataFromSettingRow(row)}
        }
        return next
    }

    private syncUiTableColumnRowToLocalStore(row: UserUiTableColumnSettingRow): void {
        if (!row?.tableKey) {
            return
        }
        const filterKey = String(row.tableKey) as FilterKey
        const cur = store.get(filterKey) || {}
        const {colsAct, colsIdx} = this.pickColArraysForLocalMerge(cur, row.columnData)
        const colOrd = this.resolveColOrdFromLocalAndServer(cur, row.columnData?.colOrd)
        store.set(filterKey, {
            ...cur,
            colsAct,
            colsIdx,
            colOrd
        })
    }

    private setUiTableColumnCache(tableKey: string, columnData: UiTableColumnSettingsData): void {
        this.uiTableColumnSettingsByPageKey[tableKey] = {columnData}
    }

    public getStorageFilter(key: FilterKey): any {
        if (this.user?.filterMemory && key) {
            const local = store.get(key)
            const tableKey = String(key)
            const cached = this.uiTableColumnSettingsByPageKey[tableKey]
            if (!cached) {
                return local
            }
            const localObj = local || {}
            const colsAct = this.pickNonEmptyLocalColsOrServer(local?.colsAct, cached.columnData.colsAct)
            const colsIdx = this.pickNonEmptyLocalColsOrServer(local?.colsIdx, cached.columnData.colsIdx)
            const colOrd = this.resolveColOrdFromLocalAndServer(localObj, cached.columnData.colOrd)
            return {
                ...(local || {}),
                colsAct,
                colsIdx,
                colOrd
            }
        }
        return null
    }

    public loadUiTableColumnSettingsInBackground(): Promise<void> {
        if (!this.user?.id || !this.user?.filterMemory) {
            return Promise.resolve()
        }
        return this.fetchUiTableColumnSettingsList()
    }

    private async fetchUiTableColumnSettingsList(): Promise<void> {
        const userId = this.user?.id
        if (!userId || !this.user?.filterMemory) {
            return
        }
        try {
            const rows = await userUiTableColumnSettingsService.list({userId})
            const listRows: UserUiTableColumnSettingRow[] = Array.isArray(rows) ? rows : []
            this.uiTableColumnSettingsByPageKey = this.buildUiTableColumnSettingsByTableKey(listRows)
            if (!this.user?.filterMemory) {
                return
            }
            for (const row of listRows) {
                this.syncUiTableColumnRowToLocalStore(row)
            }
        } catch (err) {
            Sentry.captureException(err)
        }
    }

    public setStorageFilter(key: FilterKey, value: any): void {
        if (this.user?.filterMemory && key) {
            store.set(key, value)
        }
    }

    public updateStorageFilter(filterKey: FilterKey, data: Record<string, any>, isSaveColumnsSetting = false): void {
        if (this.user?.filterMemory && filterKey) {
            let filterData = this.getStorageFilter(filterKey)
            filterData = {...(filterData || {}), ...data}
            // JSON storage drops `undefined`, so a cleared sort must persist as `null` or we fall back to server colOrd on read/persist
            if (Object.prototype.hasOwnProperty.call(data, "colOrd") && data.colOrd === undefined) {
                filterData.colOrd = null
            }
            store.set(filterKey, filterData)
            if ((isSaveColumnsSetting && ("colsAct" in data || "colsIdx" in data)) || "colOrd" in data) {
                void this.persistUiTableColumnSettings(filterKey).catch((err) => {
                    Sentry.captureException(err)
                })
            }
        }
    }

    private async persistUiTableColumnSettings(filterKey: FilterKey): Promise<void> {
        if (!this.user?.id || !filterKey || !this.user?.filterMemory) {
            return
        }

        const tableKey = String(filterKey)
        const rawLocal = store.get(filterKey) || {}
        const colOrd = this.resolveColOrdFromLocalAndServer(
            rawLocal,
            this.uiTableColumnSettingsByPageKey[tableKey]?.columnData?.colOrd
        )
        const columnData: UiTableColumnSettingsData = {
            colsAct: Array.isArray(rawLocal.colsAct) ? rawLocal.colsAct : [],
            colsIdx: Array.isArray(rawLocal.colsIdx) ? rawLocal.colsIdx : [],
            colOrd
        }

        await userUiTableColumnSettingsService.save({tableKey, columnData})
        this.setUiTableColumnCache(tableKey, columnData)
    }

    public getStorageByKey(key: string): any {
        return store.get(key)
    }

    public setStorageByKey(key: string, value: any): void {
        store.set(key, value)
    }

    public updateStorageByKey(key: string, data: Record<string, any>): void {
        const currentData = this.getStorageByKey(key)
        const newData = {...(currentData || {}), ...data}
        store.set(key, newData)
    }

    public getPriorityViewCollapsible(): {[statusId: number]: boolean} {
        if (this.user?.filterMemory) {
            return store.get(KEY_PRIORITY_COLLAPSIBLE, {})
        }
        return {}
    }

    public setPriorityViewCollapsible(state: {[statusId: number]: boolean}): void {
        if (this.user?.filterMemory) {
            store.set(KEY_PRIORITY_COLLAPSIBLE, state)
        }
    }

    public updateUserDateFormat(dateFormat: string | null) {
        if (this.user) {
            this.user.dateFormat = dateFormat
        }
    }

    public updateUserTimeFormat(timeFormat: string | null) {
        if (this.user) {
            this.user.timeFormat = timeFormat
        }
    }

    public updateUserTableView(tableView: string | null) {
        if (this.user) {
            this.user.tableView = tableView
        }
    }

    public updateUserCalendarView(view: string | null) {
        if (this.user) {
            this.user.calendarView = view
        }
    }

    public getUserDateTimeFormat() {
        let userDateTimeFormat: string
        if (this.user) {
            const userDateFormat = this.getUserDateFormat()
            const userTimeFormat = this.getUserTimeFormat()
            userDateTimeFormat = `${userDateFormat} ${userTimeFormat}`
        }
        return userDateTimeFormat
    }

    public updateUserDateTimeFormat(format: string | null) {
        if (this.user) {
            this.user.dateTimeFormat = format
        }
    }

    public formatWithDateFns(date) {
        return format(date, this.getUserDateFormat().replace(/YYYY/gi, "yyyy").replace(/DD/gi, "dd"))
    }

    /**
     * Auth
     */

    public async getOtp(data, params = {}) {
        return await authService.getOtp(data, params)
    }

    public async signIn(params: Auth.SignInParams): Promise<Auth.SignInResponse> {
        try {
            const data = await authService.signIn(params)
            if (get(data, "user")) {
                const {user, token} = data as Auth.SignInNormal
                this.setLocalStorage(user, token)
            }
            return data
        } catch (e) {
            throw e
        }
    }

    public async otpSignIn(data: Auth.SignInParams): Promise<Auth.SignInNormal> {
        try {
            const res = await authService.otpSignIn(data)
            this.setLocalStorage(res.user, res.token)
            return res
        } catch (e) {
            throw e
        }
    }

    public async updateUserInfo(): Promise<void> {
        try {
            const token = store.get("authToken")
            if (!token) return
            const user = await authService.getMe()
            this.setLocalStorage(user, token)
        } catch (e) {
            throw e
        }
    }

    public setLocalStorage(user: any, token: string) {
        const sortedProfiles = orderBy(user.profiles, "active", "desc").filter((profile) => !profile.isAccessDisabled)
        const defaultProfile = sortedProfiles.find((profile) => profile.isDefault) || head(sortedProfiles)
        store.set("authToken", token)
        this.setUserStorage(user)
        store.set("profileId", defaultProfile.id.toString())
        store.set("locale", user.displayLanguage)
        this.setUserToken(user, token, defaultProfile.id)
    }

    public async setStorage(token: string, userId: number, profileId: number) {
        try {
            store.set("authToken", token)
            store.set("profileId", profileId.toString())
            store.set("user", {id: userId})
            this.loadSession()
            const user = await authService.getMe()
            this.setUserStorage(user)
            this.photo = JSON.parse(user.photo || "{}")
            this.loadSession()
            await Promise.all([this.loadMyPermissions, this.loadDepartments()])
        } catch (e) {
            throw e
        }
    }

    public async clearStorage() {
        store.remove("authToken")
        store.remove("profileId")
        store.remove("user")
    }

    private onHistoryChange(location: Location, action: Action) {
        this.path = location.pathname
        this.location = location
    }

    public loadResourceTypes() {
        this.resourceTypes = [
            {
                id: 1,
                name: "Room"
            },
            {
                id: 2,
                name: "Lab"
            },
            {
                id: 3,
                name: "Equipment"
            },
            {
                id: 4,
                name: "Instrument"
            }
        ]
    }

    public getContactRoles() {
        return [
            {id: "Parent / Guardian", name: "Parent / Guardian"},
            {id: "Spouse", name: "Spouse"},
            {id: "Sponsor", name: "Sponsor"},
            {id: "Employer", name: "Employer"},
            {id: "Companion", name: "Companion"},
            {id: "Primary Doctor", name: "Primary Doctor"},
            {id: "Power of Attorney", name: "Power of Attorney"}
        ]
    }

    public async loadDepartments() {
        const {data: departments} = await departmentsService.getAll({sort: {orderBy: "name", orderDir: "asc"}})
        this.departments = departments.map((department: any) => ({
            ...department,
            id: department.departmentId
        }))
        await this.loadDepartmentChats()
    }

    public loadDateFormat() {
        const format = "MM/DD/YYYY"
        this.dateFormat = format
        return format
    }

    public loadTableViewDefault() {
        const view: ViewType = "standard"
        this.tableView = view
        return view
    }

    public loadCalendarViewDefault() {
        const view = "WEEK"
        this.calendarView = view
        return view
    }

    public loadTimeFormat() {
        const format = "24 H"
        this.timeFormat = format
        return format
    }

    public setPrevLocation(location: Location) {
        this.prevLocation = location
    }

    public setCurrentLocation(location: Location) {
        this.prevLocation = this.currentLocation
        this.currentLocation = location
    }

    public loadDateTimeFormat() {
        const format = "MM/DD/YYYY hh:mm A"
        this.dateTimeFormat = format
        return format
    }

    public async loadCalendars(params = {}) {
        const data = {
            filter: {
                owner_uid: this.user.id
            }
        }
        const response = await calendarService.getAllCalendars(data, params)

        this.calendars = response.data.map((calendar) => {
            if (!calendar.json_data) {
                calendar.json_data = {}
            }
            calendar.json_data.isActive = false
            return calendar
        })
        return this.calendars
    }

    public async addCalendar(data, params = {}) {
        return await calendarService.addCalendar(data, params)
    }

    public async removeEventsFromCalendar(calendarId) {
        this.calendarEvents = this.calendarEvents.filter((e) => e.calendar_id !== calendarId)
    }

    public async addEvent(data) {
        return await calendarService.addObject(data)
    }

    public async removeEvent(id) {
        return await calendarService.deleteEvent(id, {
            target_filter: "only_this"
        })
    }

    // chat
    public async loadDepartmentChats() {
        const newDepartments = this.departments.filter((department: any) => department.isDepartmentalChatEnabled)
        this.departmentsChats = newDepartments
    }

    public updateUsers = (newUsers) => {
        newUsers.forEach((item) => {
            this.users[item.id] = {
                ...(this.users[item.id] || {}),
                ...item
            }
        }, {})
    }

    public async getUsers(userIds = []) {
        const currentIds = Object.values(this.users)
            .map((user: any) => user.id)
            .filter(Boolean)
        const ids = userIds
            .filter(Boolean)
            .filter((userId) => !currentIds.includes(userId))
            .filter((userId) => !this.usersNotFound[userId])
        if (ids.length) {
            const {data} = await userServiceV3.getAllAutocomplete({
                filter: {
                    id: ids
                },
                range: {
                    pageSize: ids.length,
                    page: 1
                }
            })
            if (data.length === 0) {
                ids.forEach((id) => {
                    this.usersNotFound[id] = id
                })
            }
            data.forEach((item) => {
                this.users[item.id] = {
                    ...(this.users[item.id] || {}),
                    ...item
                }
            }, {})
        }
    }

    public getOneUser(userId) {
        if (!userId) {
            return {
                fullName: "Notifications",
                firstName: "Notifications"
            }
        }
        if (this.users[userId]?.id === undefined) {
            return this.getUsers([userId]).then(() => {
                return this.users[userId]
            })
        }
        return this.users[userId]
    }

    public async getOneCompleteUser(userId) {
        const {data} = await userServiceV3.getAll({
            filter: {
                id: [userId]
            },
            range: {
                limit: 1,
                offset: 0
            },
            linkedObjects: true
        })
        return data[0]
    }

    public updateChatOptions(key, value) {
        const newChatOptions = {
            ...this.chatOptions
        }
        newChatOptions[key] = value
        this.chatOptions = newChatOptions
    }

    public updateNotificationUnreadCount() {
        let prevTitle = document.title
        if (prevTitle.startsWith("(")) {
            prevTitle = prevTitle.replace(/^\(.*\)\s/g, "")
        }
        document.title = `${this.getStringCounter()}${prevTitle}`
    }

    public getStringCounter() {
        let count = ""
        // if (this.chatOptions[Communication.ChatOptions.UnreadNotification]) {
        //     count = `(${getUnreadCountBadge(this.chatOptions[Communication.ChatOptions.UnreadNotification])}) `
        // }

        return count
    }
}
